/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { AppReactECharts } from '../components/shared/AppReactECharts';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, getDefaultSecondaryLocale, normalizeLanguage } from '../utils/language.utils';
import { DataTable, type Column, type DataTableBulkAction, type DataTableRowActions } from '../components/shared/DataTable';
import {
    createEmptySmartFilter,
    type SmartFilterField,
    type SmartFilterValue,
} from '../components/shared/SmartFilter';
import { Pagination } from '../components/shared/Pagination';
import { Modal } from '../components/shared/Modal';
import { Alert } from '../components/shared/Alert';
import { Icon } from '../components/icons/Icon';
import { CrudActionButtons } from '../components/shared/CrudActionButtons';
import { ModalFormFooter } from '../components/shared/ModalFormFooter';
import { usePage } from '../hooks/usePage';
import { usePanelAddTrigger } from '../hooks/usePanelAddTrigger';
import { usePermission } from '../hooks/usePermission';
import { useCompany } from '../context/CompanyContext';
import { PAGE_SIZE_OPTIONS } from '../hooks/useUserPageSizePreference';
import { showQubeProDeleteConfirmDialog } from '../utils/ui-feedback.utils';
import './Translations.css';

interface TranslationItem {
    id: string;
    entityType: string;
    entityId: string;
    fieldName: string;
    locale: string;
    value: string;
    isAutoTranslated: boolean;
    isOriginal?: boolean;
    sourceLocale?: string | null;
    sourceValue?: string | null;
    updatedAt: string;
}

/** Locale → ISO 3166 ülke kodu (flagcdn.com için); bayrak görseli kurumsal görünüm */
const LOCALE_TO_COUNTRY: Record<string, string> = {
    en: 'gb',
    tr: 'tr',
    de: 'de',
    fr: 'fr',
    es: 'es',
    ar: 'sa',
    it: 'it',
    ja: 'jp',
    zh: 'cn',
    ru: 'ru',
};

function translatedLabel(value: unknown, language: string, turkish: string, english: string): string {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
    return language === 'tr' ? turkish : english;
}

function Translations() {
    const { user } = useAuth();
    const { selectedCompanyId } = useCompany();
    const scope = JSON.stringify([user?.tenantId, user?.id, user?.role, user?.isAdmin, user?.permissions, selectedCompanyId]);
    return <ScopedTranslations key={scope} />;
}

function ScopedTranslations() {
    const { language, updateLanguage, user } = useAuth();
    const t = useTranslation(language);
    const { canDelete, canUpdate } = usePermission();
    const userPermissions = user?.permissions ?? [];
    const hasPrefixedTranslationDeletePermission = userPermissions.some((permission) => (
        permission.endsWith(':translation:delete') ||
        permission.endsWith(':translation:update')
    ));
    const canDeleteTranslations = canDelete('translation') ||
        canUpdate('translation') ||
        hasPrefixedTranslationDeletePermission;
    const canUpdateTranslations = canUpdate('translation') || userPermissions.some(permission => permission.endsWith(':translation:update'));
    const errorCopy = t.common.errorOccurred || t.errors?.unknown || (language === 'tr' ? 'İşlem tamamlanamadı.' : 'The operation could not be completed.');
    const confirmLabel = t.common.confirm || (language === 'tr' ? 'Onayla' : 'Confirm');
    const labels = useMemo(() => ({
        context: translatedLabel(t.common.context, language, 'Bağlam', 'Context'),
        key: translatedLabel(t.common.key, language, 'Alan', 'Field'),
        id: translatedLabel(t.common.id, language, 'Kimlik', 'ID'),
        originalShort: translatedLabel(t.common.originalShort, language, 'Kaynak Dil', 'Source Locale'),
        originalValue: translatedLabel(t.common.originalValue, language, 'Kaynak Değer', 'Source Value'),
        locale: translatedLabel(t.common.locale, language, 'Hedef Dil', 'Target Locale'),
        value: translatedLabel(t.common.value, language, 'Çeviri', 'Translation'),
        status: translatedLabel(t.common.status, language, 'Durum', 'Status'),
        auto: translatedLabel(t.common.auto, language, 'Otomatik', 'Automatic'),
        manual: translatedLabel(t.common.manual, language, 'Manuel', 'Manual'),
        actions: translatedLabel(t.common.actions, language, 'İşlemler', 'Actions'),
        delete: translatedLabel(t.common.delete, language, 'Sil', 'Delete'),
        manageLocales: translatedLabel(t.common.manageLocales, language, 'Dilleri Yönet', 'Manage Locales'),
        updateUntranslatedFields: translatedLabel(
            t.common.updateUntranslatedFields,
            language,
            'Eksik Çevirileri Güncelle',
            'Update Missing Translations',
        ),
        autoTranslate: translatedLabel(t.common.autoTranslate, language, 'Otomatik Çevir', 'Auto Translate'),
        noData: translatedLabel(t.common.noData, language, 'Veri bulunamadı', 'No data found'),
        create: translatedLabel(t.common.create, language, 'Yeni Çeviri', 'New Translation'),
    }), [language, t.common]);

    const queryParams = new URLSearchParams({ page: '1', size: '5000' });

    const page = usePage<TranslationItem>({
        endpoint: '/api/translations',
        listEndpoint: `/api/translations?${queryParams.toString()}`,
        searchFields: ['value', 'fieldName', 'entityType'], // Bu alanlarda ara
        initialSortColumn: 'updatedAt',
        initialSortDirection: 'desc',
        language,
        updateMethod: 'put', // Upsert genellikle POST kullanır, ancak özel kaydetme işlevimiz var
    });
    const translationTableIdentity = useMemo(
        () => ({ module: 'localization', page: 'translations', component: 'main-v2' }),
        [],
    );
    const [smartFilterValue, setSmartFilterValue] = useState<SmartFilterValue>(() => createEmptySmartFilter());
    const [writePending, setWritePending] = useState(false);
    const writeLock = useRef(false);
    const active = useRef(true);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

    const write = async (
        request: () => Promise<{ data: { success?: boolean; data?: any; error?: unknown } }>,
        onSuccess: (data: any) => void,
        onError: (message: string) => void = message => page.setError(message),
    ) => {
        if (!active.current || writeLock.current) return;
        writeLock.current = true;
        setWritePending(true);
        try {
            const response = await request();
            if (!active.current) return;
            if (response.data?.success !== true) throw new Error(typeof response.data?.error === 'string' ? response.data.error : errorCopy);
            onSuccess(response.data.data);
        } catch (error: any) {
            if (!active.current) return;
            const message = error?.response?.data?.error || error?.response?.data?.message || error?.message;
            onError(typeof message === 'string' ? message : errorCopy);
        } finally {
            writeLock.current = false;
            if (active.current) setWritePending(false);
        }
    };

    usePanelAddTrigger(() => page.openModal(undefined, { asCreate: true }), canUpdateTranslations && !writePending);

    // Modal Durumu
    const [newTranslation, setNewTranslation] = useState<{
        entityType: string;
        entityId: string;
        fieldName: string;
        locale: string;
        value: string;
    }>({
        entityType: 'general',
        entityId: 'global',
        fieldName: '',
        locale: language,
        value: ''
    });

    // Otomatik Çevir modal (var olan translations tablosundaki kaynak dil → hedef dil)
    const [showAutoTranslateModal, setShowAutoTranslateModal] = useState(false);
    const [autoTranslateSourceLocale, setAutoTranslateSourceLocale] = useState(DEFAULT_LANGUAGE);
    const [autoTranslateTargetLocale, setAutoTranslateTargetLocale] = useState<string>('all');
    const [autoTranslateError, setAutoTranslateError] = useState<string | null>(null);

    // Dilleri Yönet: systemSettings (activeLocales) ile kaydedilir
    const [showManageLocalesModal, setShowManageLocalesModal] = useState(false);
    const [selectedLocales, setSelectedLocales] = useState<string[]>([]);
    const [localesLoading, setLocalesLoading] = useState(false);
    const [localesReady, setLocalesReady] = useState(false);
    const localeRead = useRef(0);
    const [manageLocalesError, setManageLocalesError] = useState<string | null>(null);

    // systemSettings activeLocales (Dilleri Yönet’teki seçili diller) – hedef dil listesi için
    const [activeLocalesFromSettings, setActiveLocalesFromSettings] = useState<string[]>([]);

    // Sync: orijinal dil seçimi modalı
    const [showSyncSourceModal, setShowSyncSourceModal] = useState(false);
    const [syncSourceLocale, setSyncSourceLocale] = useState(DEFAULT_LANGUAGE);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);

    // Header’daki seçili dile göre dil isimleri (Layout’taki dil seçicisi ile uyumlu)
    const localeOptions = useMemo(() => {
        const names = t.common.languageNames ?? {};
        return SUPPORTED_LANGUAGES.map((code) => ({
            value: code,
            label: names[code] ?? code,
        }));
    }, [t.common.languageNames]);

    const handleSave = async (item: TranslationItem, newValue: string) => {
        if (!canUpdateTranslations) return;
        await write(() => axios.post('/api/translations/upsert', {
            translations: [{ entityType: item.entityType, entityId: item.entityId, fieldName: item.fieldName, locale: item.locale, value: newValue }],
        }), () => page.refresh());
    };

    const closeCreate = () => { if (!writeLock.current) page.closeModal(); };
    const handleAddNew = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canUpdateTranslations || !newTranslation.fieldName.trim() || !newTranslation.value.trim()) return;
        setCreateError(null);
        await write(() => axios.post('/api/translations/upsert', { translations: [newTranslation] }), () => {
            page.closeModal();
            setNewTranslation({ entityType: 'general', entityId: 'global', fieldName: '', locale: language, value: '' });
            page.refresh();
        }, setCreateError);
    };

    const deleteSelectedLabel = t.common.deleteSelected || (language === 'tr' ? 'Seçili sil' : 'Delete selected');
    const handleDeleteTranslation = useCallback(async (item: TranslationItem) => {
        if (!canDeleteTranslations || writeLock.current) return;
        const message = language === 'tr'
            ? `"${item.fieldName}" alanının ${item.locale.toUpperCase()} çeviri kaydı silinecek. Bu işlem geri alınamaz.`
            : `The ${item.locale.toUpperCase()} translation for "${item.fieldName}" will be deleted. This action cannot be undone.`;
        await page.deleteItem(item.id, { confirmMessage: message, confirmLabel: t.common.delete });
    }, [canDeleteTranslations, language, page, t.common.delete]);

    const handleBulkDeleteTranslations = async (selectedIds: Array<string | number>, clearSelection: () => void) => {
        if (!canDeleteTranslations || writeLock.current || selectedIds.length === 0) return;
        const confirmed = await showQubeProDeleteConfirmDialog({
            language,
            message: language === 'tr' ? `${selectedIds.length} çeviri kaydı silinecek. Bu işlem geri alınamaz.` : `${selectedIds.length} translation records will be deleted. This action cannot be undone.`,
            confirmLabel: deleteSelectedLabel,
        });
        if (!confirmed || !active.current) return;
        await write(() => axios.post('/api/translations/bulk-delete', { ids: selectedIds }), data => {
            clearSelection();
            const deleted = data?.deleted ?? selectedIds.length;
            page.setSuccess(language === 'tr' ? `${deleted} çeviri kaydı silindi.` : `${deleted} translation records deleted.`);
            page.refresh();
        });
    };

    const fetchActiveLocales = async (): Promise<string[] | null> => {
        try {
            const res = await axios.get('/api/translations/active-locales');
            if (!active.current) return null;
            if (res.data?.success !== true || !Array.isArray(res.data.data?.locales)) throw new Error(errorCopy);
            const list = res.data.data.locales as string[];
            setActiveLocalesFromSettings(list);
            return list;
        } catch {
            if (active.current) page.setError(errorCopy);
            return null;
        }
    };

    const handleOpenAutoTranslate = async () => {
        if (!canUpdateTranslations || writeLock.current) return;
        const activeLocales = await fetchActiveLocales();
        if (!active.current || activeLocales === null) return;
        setAutoTranslateError(null);
        const source = normalizeLanguage(page.raw.find(item => item.isOriginal)?.locale || (activeLocales.includes(DEFAULT_LANGUAGE) ? DEFAULT_LANGUAGE : activeLocales[0] || DEFAULT_LANGUAGE));
        setAutoTranslateSourceLocale(source);
        setAutoTranslateTargetLocale('all');
        setShowAutoTranslateModal(true);
    };

    const handleAutoTranslateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canUpdateTranslations) return;
        if (autoTranslateTargetLocale === autoTranslateSourceLocale) {
            setAutoTranslateError(t.common.sourceAndTargetMustDiffer || (language === 'tr' ? 'Kaynak ve hedef dil farklı olmalı.' : 'Source and target languages must differ.'));
            return;
        }
        setAutoTranslateError(null);
        await write(() => axios.post('/api/translations/auto-translate', { sourceLocale: autoTranslateSourceLocale, targetLocale: autoTranslateTargetLocale }, { timeout: 180000 }), data => {
            setShowAutoTranslateModal(false);
            page.refresh();
            page.setSuccess((t.common.autoTranslateSuccessCount || '{count} items auto-translated.').replace('{count}', String(data?.processed ?? 0)));
        }, setAutoTranslateError);
    };

    const handleOpenSyncSourceModal = async () => {
        if (!canUpdateTranslations || writeLock.current) return;
        const locales = await fetchActiveLocales();
        if (!active.current || locales === null) return;
        setSyncSourceLocale(normalizeLanguage(locales.includes(language) ? language : locales[0] || DEFAULT_LANGUAGE));
        setSyncError(null);
        setShowSyncSourceModal(true);
    };
    const handleConfirmSyncContent = async () => {
        if (!canUpdateTranslations) return;
        setSyncError(null);
        await write(() => axios.post('/api/translations/sync-content', { sourceLocale: syncSourceLocale }), data => {
            setShowSyncSourceModal(false);
            let message = (t.common.syncEntriesResult || 'Synced {count} new translation entries.').replace('{count}', String(data?.created ?? 0));
            if (data?.deleted > 0) message += ' ' + (t.common.syncDeletedResult || '{count} orphaned entries removed.').replace('{count}', String(data.deleted));
            page.setSuccess(message);
            page.refresh();
        }, setSyncError);
    };

    const handleOpenManageLocales = async () => {
        if (!canUpdateTranslations || writeLock.current) return;
        const generation = ++localeRead.current;
        setManageLocalesError(null);
        setSelectedLocales([]);
        setLocalesReady(false);
        setLocalesLoading(true);
        setShowManageLocalesModal(true);
        try {
            const res = await axios.get('/api/translations/active-locales');
            if (!active.current || generation !== localeRead.current) return;
            if (res.data?.success !== true || !Array.isArray(res.data.data?.locales) || res.data.data?.migrationRequired) throw new Error(errorCopy);
            setSelectedLocales(res.data.data.locales);
            setLocalesReady(true);
        } catch {
            if (active.current && generation === localeRead.current) setManageLocalesError(errorCopy);
        } finally {
            if (active.current && generation === localeRead.current) setLocalesLoading(false);
        }
    };
    const handleToggleLocale = (value: string) => {
        if (!canUpdateTranslations || !localesReady || writeLock.current) return;
        setSelectedLocales(prev => prev.includes(value) ? prev.filter(locale => locale !== value) : [...prev, value]);
    };
    const handleSaveManageLocales = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canUpdateTranslations || !localesReady) return;
        setManageLocalesError(null);
        await write(() => axios.put('/api/translations/active-locales', { locales: selectedLocales }), () => setShowManageLocalesModal(false), setManageLocalesError);
    };
    const handleSelectLanguage = async (localeValue: string) => {
        if (!active.current || writeLock.current) return;
        try { await updateLanguage(localeValue as any); if (active.current) setShowManageLocalesModal(false); } catch { /* Existing language is retained. */ }
    };

    const entityTypeOptions = useMemo(() => {
        const set = new Set<string>();
        page.raw.forEach((item) => set.add(item.entityType));
        return Array.from(set).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
    }, [page.raw]);

    const fieldNameOptions = useMemo(() => {
        const set = new Set<string>();
        page.raw.forEach((item) => set.add(item.fieldName));
        return Array.from(set).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
    }, [page.raw]);

    const localeOptionsFromData = useMemo(() => {
        const set = new Set<string>();
        page.raw.forEach((item) => set.add(item.locale));
        return Array.from(set)
            .sort((a, b) => a.localeCompare(b))
            .map((value) => {
                const found = localeOptions.find((o) => o.value === value);
                return { value, label: found ? found.label : value.toUpperCase() };
            });
    }, [page.raw, localeOptions]);

    const sourceLocaleOptionsFromData = useMemo(() => {
        const set = new Set<string>();
        page.raw.forEach((item) => {
            if (item.sourceLocale) set.add(item.sourceLocale);
        });
        return Array.from(set)
            .sort((a, b) => a.localeCompare(b))
            .map((value) => {
                const found = localeOptions.find((o) => o.value === value);
                return { value, label: found ? found.label : value.toUpperCase() };
            });
    }, [page.raw, localeOptions]);

    // Kaynak dil (Orijinal dil): yalnızca systemSettings activeLocales (Dilleri Yönet’teki seçili diller)
    const sourceLocaleOptionsForAutoTranslate = useMemo(() => {
        return activeLocalesFromSettings.map((code) => {
            const found = localeOptions.find((o) => o.value === code);
            return { value: code, label: found ? found.label : code.toUpperCase() };
        });
    }, [activeLocalesFromSettings, localeOptions]);

    // Hedef dil: systemSettings activeLocales (Dilleri Yönet’teki seçili diller), kaynak hariç; boşsa tablo/tüm diller yedek
    const targetLocaleOptionsForAutoTranslate = useMemo(() => {
        const all = { value: 'all', label: t.common.allTargetLanguages || t.common.all };
        if (activeLocalesFromSettings.length > 0) {
            const opts = activeLocalesFromSettings
                .filter((code) => code !== autoTranslateSourceLocale)
                .map((code) => {
                    const found = localeOptions.find((o) => o.value === code);
                    return { value: code, label: found ? found.label : code.toUpperCase() };
                });
            if (opts.length > 0) return [all, ...opts];
        }
        const base = localeOptionsFromData.length > 0 ? localeOptionsFromData : localeOptions;
        const filtered = base.filter((opt) => opt.value !== autoTranslateSourceLocale);
        return [all, ...(filtered.length > 0 ? filtered : localeOptions.filter((opt) => opt.value !== autoTranslateSourceLocale))];
    }, [activeLocalesFromSettings, localeOptionsFromData, localeOptions, autoTranslateSourceLocale, t.common.allTargetLanguages]);

    // Sayfa yüklendiğinde activeLocales’i al (Orijinal/Hedef dil listeleri için)
    useEffect(() => {
        fetchActiveLocales();
    }, []);

    // Otomatik çevir modalı açıkken kaynak/hedef listede yoksa seçimleri güncelle
    useEffect(() => {
        if (!showAutoTranslateModal) return;
        const sourceOpts = sourceLocaleOptionsForAutoTranslate;
        const targetOpts = targetLocaleOptionsForAutoTranslate;
        const sourceValid = sourceOpts.some((o) => o.value === autoTranslateSourceLocale);
        const targetValid = targetOpts.some((o) => o.value === autoTranslateTargetLocale);
        if (!sourceValid && sourceOpts.length > 0) {
            const src = normalizeLanguage(sourceOpts[0].value);
            setAutoTranslateSourceLocale(src);
            const other = targetLocaleOptionsForAutoTranslate[0];
            setAutoTranslateTargetLocale(other?.value || 'all');
        } else if (!targetValid || autoTranslateTargetLocale === autoTranslateSourceLocale) {
            setAutoTranslateTargetLocale(targetOpts[0]?.value || 'all');
        }
    }, [showAutoTranslateModal, sourceLocaleOptionsForAutoTranslate, autoTranslateSourceLocale, autoTranslateTargetLocale, targetLocaleOptionsForAutoTranslate]);

    // Grafikler: seçili dillere göre çeviri oranı (toplam anahtar sayısına göre çevrildi / eksik)
    const totalUniqueKeys = useMemo(() => {
        const keys = new Set(page.raw.map((r) => `${r.entityType}:${r.entityId}:${r.fieldName}`));
        return keys.size;
    }, [page.raw]);

    const translationChartsData = useMemo(() => {
        if (activeLocalesFromSettings.length === 0 || totalUniqueKeys === 0) return [];
        return activeLocalesFromSettings.map((localeCode) => {
            const translatedCount = page.raw.filter((r) => r.locale === localeCode).length;
            const missingCount = Math.max(0, totalUniqueKeys - translatedCount);
            const label = localeOptions.find((o) => o.value === localeCode)?.label ?? localeCode.toUpperCase();
            const data = [
                ...Array(translatedCount).fill(null).map(() => ({ isAutoTranslated: true })),
                ...Array(missingCount).fill(null).map(() => ({ isAutoTranslated: false })),
            ];
            return { localeCode, label, data, translatedCount, missingCount };
        });
    }, [activeLocalesFromSettings, page.raw, totalUniqueKeys, localeOptions]);

    const { theme } = useTheme();
    const [translationChartOpen, setTranslationChartOpen] = useState(false);

    const translationRatesChartOption = useMemo(() => {
        if (translationChartsData.length === 0) return null;
        const translatedLabel = t.common.translated ?? 'Çevrildi';
        const missingLabel = t.common.missing ?? 'Eksik';
        const labels = translationChartsData.map((d) => d.label);
        const translatedValues = translationChartsData.map((d) => d.translatedCount);
        const missingValues = translationChartsData.map((d) => d.missingCount);
        const textColor = 'var(--text-primary)';
        const gridColor = 'var(--chart-grid)';
        return {
            backgroundColor: 'transparent',
            textStyle: { color: textColor },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                backgroundColor: 'var(--card-bg)',
                borderColor: 'var(--border-color)',
                textStyle: { color: textColor },
            },
            legend: {
                data: [translatedLabel, missingLabel],
                bottom: 0,
                textStyle: { color: textColor },
            },
            grid: { left: '3%', right: '4%', bottom: '15%', top: 10, containLabel: true },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: { color: textColor },
                axisLine: { lineStyle: { color: gridColor } },
            },
            yAxis: {
                type: 'value',
                axisLabel: { color: textColor },
                splitLine: { lineStyle: { color: gridColor, type: 'dashed' } },
            },
            series: [
                { name: translatedLabel, type: 'bar', stack: 'total', data: translatedValues, itemStyle: { color: 'var(--status-active)' } },
                { name: missingLabel, type: 'bar', stack: 'total', data: missingValues, itemStyle: { color: 'var(--status-suspended)' } },
            ],
        };
    }, [translationChartsData, language, theme]);

    // Rapor grafikleri: tablo ve dil bazlı dağılım (pasta)
    const distributionByTable = useMemo(() => {
        if (!page.raw?.length) return [];
        const map = new Map<string, number>();
        page.raw.forEach((r) => map.set(r.entityType, (map.get(r.entityType) ?? 0) + 1));
        return Array.from(map.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [page.raw]);

    const distributionByLocale = useMemo(() => {
        if (!page.raw?.length) return [];
        const map = new Map<string, number>();
        page.raw.forEach((r) => map.set(r.locale, (map.get(r.locale) ?? 0) + 1));
        return Array.from(map.entries())
            .map(([localeCode, value]) => ({
                name: localeOptions.find((o) => o.value === localeCode)?.label ?? localeCode.toUpperCase(),
                value,
            }))
            .sort((a, b) => b.value - a.value);
    }, [page.raw, localeOptions]);

    const PIE_COLORS = [
        'var(--chart-severity-info)',
        'var(--accent-primary)',
        'var(--chart-severity-warning)',
        'var(--chart-severity-danger)',
        'var(--stat-icon-violet)',
        'var(--status-active)',
        'var(--status-expired)',
        'var(--text-tertiary)',
    ];

    const buildPieOption = (chartData: { name: string; value: number }[], title: string) => {
        if (!chartData.length) return null;
        const textColor = 'var(--text-primary)';
        return {
            backgroundColor: 'transparent',
            textStyle: { color: textColor },
            title: {
                text: title,
                left: 'center',
                top: 8,
                textStyle: { color: textColor, fontSize: 14 },
            },
            tooltip: {
                trigger: 'item',
                backgroundColor: 'var(--card-bg)',
                borderColor: 'var(--border-color)',
                textStyle: { color: textColor },
                formatter: '{b}: {c} ({d}%)',
            },
            legend: {
                type: 'scroll',
                orient: 'horizontal',
                left: 12,
                right: 12,
                bottom: 0,
                itemWidth: 12,
                itemHeight: 8,
                itemGap: 10,
                textStyle: { color: textColor },
            },
            series: [
                {
                    type: 'pie',
                    radius: ['38%', '66%'],
                    center: ['50%', '43%'],
                    avoidLabelOverlap: true,
                    minShowLabelAngle: 3,
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: 'var(--chart-pie-border)',
                        borderWidth: 2,
                    },
                    label: { show: true, formatter: '{b}: {c}', color: textColor },
                    labelLayout: { hideOverlap: true },
                    labelLine: { show: true, lineStyle: { color: 'var(--chart-axis-mute)' } },
                    data: chartData.map((d, i) => ({
                        ...d,
                        itemStyle: { color: PIE_COLORS[i % PIE_COLORS.length] },
                    })),
                },
            ],
        };
    };

    const reportChartByTableOption = useMemo(
        () => buildPieOption(distributionByTable, t.common.distributionByTable ?? 'Tablo dağılımı'),
        [distributionByTable, theme, t.common.distributionByTable],
    );
    const reportChartByLocaleOption = useMemo(
        () => buildPieOption(distributionByLocale, t.common.distributionByLocale ?? 'Dil dağılımı'),
        [distributionByLocale, theme, localeOptions, t.common.distributionByLocale],
    );

    // Sütunlar
    const columns: Column<TranslationItem>[] = useMemo(() => [
        {
            key: 'entityType' as const,
            title: labels.context,
            width: '150px',
            sortable: true,
            filterable: true,
            filterType: 'select',
            filterOptions: entityTypeOptions,
            filterValue: page.columnFilters.entityType || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('entityType', val),
            render: (item: TranslationItem) => (
                <strong>{item.entityType}</strong>
            )
        },
        {
            key: 'fieldName' as const,
            title: labels.key,
            width: '150px',
            sortable: true,
            filterable: true,
            filterType: 'select',
            filterOptions: fieldNameOptions,
            filterValue: page.columnFilters.fieldName || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('fieldName', val),
            render: (item: TranslationItem) => (
                <strong>{item.fieldName}</strong>
            )
        },
        {
            key: 'entityId' as const,
            title: labels.id,
            width: '76px',
            align: 'center',
            sortable: true,
            filterable: true,
            filterType: 'text',
            filterValue: page.columnFilters.entityId || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('entityId', val),
            render: (item: TranslationItem) => (
                <strong>{item.entityId}</strong>
            )
        },
        {
            key: 'sourceLocale' as const,
            title: labels.originalShort,
            width: '96px',
            align: 'center',
            filterable: true,
            filterType: 'select',
            filterOptions: sourceLocaleOptionsFromData,
            filterValue: page.columnFilters.sourceLocale || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('sourceLocale', val),
            sortable: true,
            render: (item: TranslationItem) => {
                const code = item.sourceLocale ? item.sourceLocale.toUpperCase() : null;
                if (!code) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                return (
                    <span className="badge badge-secondary" style={{ textTransform: 'uppercase' }}>
                        {code}
                    </span>
                );
            }
        },
        {
            key: 'sourceValue' as any,
            title: labels.originalValue,
            width: '256px',
            filterable: true,
            filterType: 'text',
            filterValue: page.columnFilters.sourceValue || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('sourceValue', val),
            render: (item: TranslationItem) => {
                const val = item.sourceValue;
                return (
                    <span style={{
                        color: 'var(--text-secondary)',
                        display: 'block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={val || ''}>
                        {val || '—'}
                    </span>
                );
            }
        },
        {
            key: 'locale' as const,
            title: labels.locale,
            width: '92px',
            align: 'center',
            filterable: true,
            filterType: 'select',
            filterOptions: localeOptionsFromData,
            filterValue: page.columnFilters.locale || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('locale', val),
            render: (item: TranslationItem) => (
                <span className="badge badge-secondary" style={{ textTransform: 'uppercase' }}>
                    {item.locale}
                </span>
            )
        },
        {
            key: 'value' as const,
            title: labels.value,
            width: '256px',
            filterable: true,
            filterType: 'text',
            filterValue: page.columnFilters.value || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('value', val),
            render: (item: TranslationItem) => (
                <input
                    type="text"
                    defaultValue={item.value}
                    disabled={!canUpdateTranslations || writePending}
                    aria-label={labels.value}
                    className="form-control"
                    style={{
                        width: '100%',
                        background: 'transparent',
                        border: '1px solid transparent',
                        transition: 'all 0.2s',
                        fontSize: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                    onBlur={(e) => {
                        e.target.style.borderColor = 'transparent';
                        if (e.target.value !== item.value) {
                            handleSave(item, e.target.value);
                        }
                    }}
                />
            )
        },
        {
            key: 'isAutoTranslated' as const,
            title: labels.status,
            width: '132px',
            align: 'center',
            sortable: true,
            filterable: true,
            filterType: 'select',
            filterOptions: [
                { label: labels.auto, value: 'true' },
                { label: labels.manual, value: 'false' }
            ],
            filterValue: page.columnFilters.isAutoTranslated || '',
            onFilterChange: (val: string) => page.handleColumnFilterChange('isAutoTranslated', val),
            render: (item: TranslationItem) => item.isAutoTranslated ? (
                <span style={{ color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    <Icon name="lightbulb" size={14} /> {labels.auto}
                </span>
            ) : (
                <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    <Icon name="checkCircle" size={14} /> {labels.manual}
                </span>
            )
        }
    ] as Column<TranslationItem>[], [labels, page.columnFilters, page.handleColumnFilterChange, page.raw, localeOptionsFromData, canUpdateTranslations, writePending]);

    const translationBulkActions = useMemo<DataTableBulkAction<TranslationItem>[]>(() => [
        {
            key: 'delete-selected',
            label: deleteSelectedLabel,
            icon: 'delete',
            variant: 'danger',
            hidden: !canDeleteTranslations,
            disabled: ({ selectedIds }) => writePending || selectedIds.length === 0,
            onClick: async ({ selectedIds, clearSelection }) => {
                await handleBulkDeleteTranslations(selectedIds, clearSelection);
            },
        },
    ], [canDeleteTranslations, writePending, deleteSelectedLabel, handleBulkDeleteTranslations]);

    const translationRowActions = useMemo<DataTableRowActions<TranslationItem>>(
        () => ({
            ariaLabel: labels.actions,
            render: (item) => (
                <CrudActionButtons
                    language={language as any}
                    onDelete={canDeleteTranslations ? () => void handleDeleteTranslation(item) : undefined}
                    disableDelete={writePending}
                    deleteTitle={labels.delete}
                />
            ),
        }),
        [canDeleteTranslations, writePending, handleDeleteTranslation, labels.actions, labels.delete, language],
    );

    const filtersApplied = useMemo(() => {
        const hasSearch = page.searchTerm.trim().length > 0;
        const hasColumnFilters = Object.values(page.columnFilters).some((v) => v?.trim());
        return hasSearch || hasColumnFilters;
    }, [page.searchTerm, page.columnFilters]);

    const smartFilterFields = useMemo<SmartFilterField[]>(() => [
        { key: 'entityType', label: labels.context, type: 'select', options: entityTypeOptions },
        { key: 'fieldName', label: labels.key, type: 'select', options: fieldNameOptions },
        { key: 'entityId', label: labels.id, type: 'text' },
        { key: 'sourceLocale', label: labels.originalShort, type: 'select', options: sourceLocaleOptionsFromData },
        { key: 'sourceValue', label: labels.originalValue, type: 'text' },
        { key: 'locale', label: labels.locale, type: 'select', options: localeOptionsFromData },
        { key: 'value', label: labels.value, type: 'text' },
        {
            key: 'isAutoTranslated',
            label: labels.status,
            type: 'select',
            options: [
                { label: labels.auto, value: 'true' },
                { label: labels.manual, value: 'false' },
            ],
        },
    ], [entityTypeOptions, fieldNameOptions, labels, localeOptionsFromData, sourceLocaleOptionsFromData]);

    const applySmartFilter = useCallback((value: SmartFilterValue) => {
        setSmartFilterValue(value);
        page.setSearchTerm(value.quickSearch?.trim() || '');

        const nextColumnFilters: Record<string, string> = {};
        value.rules.forEach((rule) => {
            const ruleValue = String(rule.value ?? '').trim();
            if (!ruleValue) return;
            nextColumnFilters[rule.field] = ruleValue;
        });

        page.setColumnFilters(nextColumnFilters);
        page.setCurrentPage(1);
    }, [page]);

    const clearSmartFilter = useCallback(() => {
        setSmartFilterValue(createEmptySmartFilter());
        page.clearFilters();
    }, [page]);

    return (
        <div className="page-container translations-page">
            <Alert
                type="error"
                message={page.error}
                placement="banner"
                onDismiss={() => page.setError(null)}
            />
            <Alert
                type="success"
                message={page.success}
                placement="banner"
                onDismiss={() => page.setSuccess(null)}
            />
            {/* Tek blok: Çeviri raporu — bar (seçili dillere göre oran) + tablo/dil pasta grafikleri */}
            {(translationChartsData.length > 0 || distributionByTable.length > 0 || distributionByLocale.length > 0) && (
                <div className={`translations-chart-single product-status-chart ${translationChartOpen ? 'open' : 'collapsed'}`}>
                    <div className="chart-header">
                        <h3>{t.common.translationReport ?? 'Çeviri Durum Dağılımı'}</h3>
                        <button
                            type="button"
                            className="chart-toggle-btn"
                            onClick={() => setTranslationChartOpen((o) => !o)}
                            aria-label={translationChartOpen ? t.common.hide : t.common.show}
                        >
                            <span style={{ marginRight: '6px' }}>{translationChartOpen ? t.common.hide : t.common.show}</span>
                            <Icon name={translationChartOpen ? 'chevronUp' : 'chevronDown'} size={16} />
                        </button>
                    </div>
                    {translationChartOpen && (
                        <div className="chart-content">
                            {translationRatesChartOption && (
                                <div className="translations-bar-wrap">
                                    <AppReactECharts
                                        option={translationRatesChartOption}
                                        style={{ height: '240px', width: '100%' }}
                                    />
                                </div>
                            )}
                            {(reportChartByTableOption || reportChartByLocaleOption) && (
                                <div className="translations-report-grid">
                                    {reportChartByTableOption && (
                                        <div className="translations-report-pie-wrap">
                                            <AppReactECharts
                                                option={reportChartByTableOption}
                                                style={{ height: '260px', width: '100%' }}
                                            />
                                        </div>
                                    )}
                                    {reportChartByLocaleOption && (
                                        <div className="translations-report-pie-wrap">
                                            <AppReactECharts
                                                option={reportChartByLocaleOption}
                                                style={{ height: '260px', width: '100%' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Extra Toolbar for Custom Actions */}
            <div className="translations-actions">
                <div className="help-popover-container" tabIndex={0} style={{ marginRight: 'auto' }}>
                    <Icon name="info" size={18} />
                    <div className="help-popover-card">
                        <h4>
                            <Icon name="info" size={16} style={{ color: 'var(--accent-primary)' }} />
                            {t.common.autoTranslateScope}
                        </h4>
                        <ul>
                            <li><strong>UserGroup</strong> <span>name, description</span></li>
                            <li><strong>Organization</strong> <span>name, description</span></li>
                            <li><strong>Company</strong> <span>name</span></li>
                            <li><strong>Role</strong> <span>name, description</span></li>
                            <li><strong>Permission</strong> <span>name, description</span></li>
                            <li><strong>DataSource</strong> <span>name</span></li>
                        </ul>
                        <div className="help-popover-footer">
                            {t.common.updateUntranslatedFieldsHint}
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn-primary"
                    onClick={handleOpenManageLocales}
                    disabled={page.loading || !canUpdateTranslations || writePending}
                    title={labels.manageLocales}
                    aria-label={labels.manageLocales}
                >
                    <Icon name="language" size={18} />
                    <span>{labels.manageLocales}</span>
                </button>
                <button
                    className="btn-secondary"
                    onClick={handleOpenSyncSourceModal}
                    disabled={page.loading || !canUpdateTranslations || writePending}
                    title={labels.updateUntranslatedFields}
                    aria-label={labels.updateUntranslatedFields}
                >
                    <Icon name="refresh" size={18} />
                    <span>{labels.updateUntranslatedFields}</span>
                </button>
                <button
                    className="btn-secondary"
                    onClick={handleOpenAutoTranslate}
                    disabled={page.loading || !canUpdateTranslations || writePending}
                    title={labels.autoTranslate}
                    aria-label={labels.autoTranslate}
                >
                    <Icon name="lightbulb" size={18} />
                    <span>{labels.autoTranslate}</span>
                </button>
            </div>

            {/* Table */}
            <DataTable
                data={page.data}
                columns={columns}
                loading={page.loading}
                emptyMessage={labels.noData}
                tableIdentity={translationTableIdentity}
                smartFilter={{
                  fields: smartFilterFields,
                  value: smartFilterValue,
                  onApply: applySmartFilter,
                  onClear: clearSmartFilter,
                  onAddClick: () => page.openModal(undefined, { asCreate: true }),
                  addDisabled: !canUpdateTranslations || writePending,
                  addTitle: labels.create,
                  hasExternalFilters: filtersApplied,
                }}
                onRefresh={page.loadData}
                bulkActions={translationBulkActions}
                rowActions={canDeleteTranslations ? translationRowActions : false}
                sortColumn={page.sortColumn}
                sortDirection={page.sortDirection}
                onSort={(col) => {
                    page.handleSort(col as any);
                    page.setCurrentPage(1);
                }}
                isSortManuallyChanged={page.isSortManuallyChanged}
                language={language as any}
            />

            {/* Pagination */}
            {page.isPageSizeResolved && !page.loading && (
                <Pagination
                    currentPage={page.currentPage}
                    totalPages={page.totalPages}
                    pageSize={page.pageSize}
                    totalItems={page.totalItems}
                    onPageChange={page.setCurrentPage}
                    onPageSizeChange={page.setPageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    language={language as any}
                />
            )}

            {/* Otomatik Çevir Modal */}
            <Modal
                isOpen={showAutoTranslateModal}
                onClose={() => !writePending && setShowAutoTranslateModal(false)}
                title={labels.autoTranslate}
                size="sm"
                footer={
                    <div className="modal-footer-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setShowAutoTranslateModal(false)}
                            disabled={writePending}
                        >
                            {t.common.cancel}
                        </button>
                        <button
                            type="submit"
                            form="auto-translate-form"
                            className="btn-primary"
                            disabled={writePending}
                        >
                            {writePending ? (t.common.processing || t.common.loading) : confirmLabel}
                        </button>
                    </div>
                }
            >
                <form id="auto-translate-form" onSubmit={handleAutoTranslateSubmit} className="auto-translate-form">
                    <p className="auto-translate-description">
                        {t.common.enterTargetLanguage}
                    </p>
                    <div className="form-group mb-3">
                        <label className="form-label">{t.common.sourceLanguage || (language === 'tr' ? 'Kaynak Dil' : 'Source Language')}</label>
                        <select
                            className="form-control"
                            value={sourceLocaleOptionsForAutoTranslate.length === 0 ? '' : (sourceLocaleOptionsForAutoTranslate.some((o) => o.value === autoTranslateSourceLocale) ? autoTranslateSourceLocale : sourceLocaleOptionsForAutoTranslate[0]?.value ?? '')}
                            onChange={(e) => {
                                const next = normalizeLanguage(e.target.value);
                                setAutoTranslateSourceLocale(next);
                                if (autoTranslateTargetLocale === next) {
                                    const other = sourceLocaleOptionsForAutoTranslate.find((o) => o.value !== next);
                                    setAutoTranslateTargetLocale(other?.value || 'all');
                                }
                            }}
                            disabled={writePending}
                            aria-label={t.common.sourceLanguage || (language === 'tr' ? 'Kaynak Dil' : 'Source Language')}
                        >
                            {sourceLocaleOptionsForAutoTranslate.length === 0 ? (
                                <option value="">{t.common.selectLanguagesFirst}</option>
                            ) : (
                                sourceLocaleOptionsForAutoTranslate.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))
                            )}
                        </select>
                        <p className="form-hint" style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                            {t.common.sourceLanguageLabel}
                        </p>
                    </div>
                    <div className="form-group mb-4">
                        <label className="form-label">{t.common.targetLanguage || (language === 'tr' ? 'Hedef Dil' : 'Target Language')}</label>
                        <select
                            className="form-control"
                            value={targetLocaleOptionsForAutoTranslate.some((o) => o.value === autoTranslateTargetLocale) ? autoTranslateTargetLocale : (targetLocaleOptionsForAutoTranslate[0]?.value ?? getDefaultSecondaryLocale(autoTranslateSourceLocale))}
                            onChange={(e) => setAutoTranslateTargetLocale(e.target.value)}
                            disabled={writePending}
                            aria-label={t.common.targetLanguage || (language === 'tr' ? 'Hedef Dil' : 'Target Language')}
                        >
                            {targetLocaleOptionsForAutoTranslate.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    {autoTranslateError && (
                        <div className="auto-translate-error" role="alert">
                            {autoTranslateError}
                        </div>
                    )}
                </form>
            </Modal>

            {/* Dilleri Yönet: systemSettings (activeLocales) ile kaydedilir */}
            <Modal
                isOpen={showManageLocalesModal}
                onClose={() => !writePending && setShowManageLocalesModal(false)}
                title={labels.manageLocales}
                size="md"
                footer={
                    <div className="modal-footer-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setShowManageLocalesModal(false)}
                            disabled={writePending}
                        >
                            {t.common.cancel}
                        </button>
                        <button
                            type="submit"
                            form="manage-locales-form"
                            className="btn-primary"
                            disabled={writePending || !localesReady}
                        >
                            {writePending ? t.common.saving : t.common.save}
                        </button>
                    </div>
                }
            >
                <form id="manage-locales-form" onSubmit={handleSaveManageLocales} className="auto-translate-form manage-locales-form">
                    {localesLoading && <p role="status">{t.common.loading}</p>}
                    <p className="auto-translate-description">
                        {t.common.activeLocalesDescription}
                    </p>
                    <div className="manage-locales-list">
                        {localeOptions.map((opt) => {
                            const isActive = selectedLocales.includes(opt.value);
                            const isCurrentLang = language === opt.value;
                            return (
                                <div key={opt.value} className="manage-locale-row">
                                    <span className="manage-locale-badge" aria-hidden="true" title={opt.label}>
                                        {LOCALE_TO_COUNTRY[opt.value] ? (
                                            <img
                                                src={`https://flagcdn.com/w80/${LOCALE_TO_COUNTRY[opt.value]}.png`}
                                                alt=""
                                                className="manage-locale-flag-img"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <Icon name="language" size={24} />
                                        )}
                                    </span>
                                    <span
                                        className="manage-locale-label"
                                        style={{ cursor: 'pointer', flex: 1 }}
                                        onClick={() => !writePending && handleSelectLanguage(opt.value)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && !writePending && handleSelectLanguage(opt.value)}
                                    >
                                        {opt.label}
                                        {isCurrentLang && (
                                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>
                                                ({t.common.active})
                                            </span>
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={isActive}
                                        aria-label={`${opt.label} ${isActive ? t.common.active : t.common.inactive}`}
                                        className={`manage-locale-toggle ${isActive ? 'is-on' : ''}`}
                                        onClick={() => !writePending && handleToggleLocale(opt.value)}
                                        disabled={writePending || !localesReady}
                                    >
                                        <span className="manage-locale-toggle-thumb" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    {manageLocalesError && (
                        <div className="auto-translate-error" role="alert">
                            {manageLocalesError}
                        </div>
                    )}
                </form>
            </Modal>

            {/* Sync: Orijinal dil seçimi modalı */}
            <Modal
                isOpen={showSyncSourceModal}
                onClose={() => !writePending && setShowSyncSourceModal(false)}
                title={labels.updateUntranslatedFields}
                size="sm"
                footer={
                    <div className="modal-footer-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setShowSyncSourceModal(false)}
                            disabled={writePending}
                        >
                            {t.common.cancel}
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={handleConfirmSyncContent}
                            disabled={writePending}
                        >
                            {writePending ? t.common.saving : confirmLabel}
                        </button>
                    </div>
                }
            >
                <p className="auto-translate-description" style={{ marginBottom: 16 }}>
                    {t.common.selectSourceLanguageForSync}
                </p>
                <div className="form-group">
                    <label className="form-label">{t.common.sourceLanguage || (language === 'tr' ? 'Kaynak Dil' : 'Source Language')}</label>
                    <select
                        className="form-control"
                        value={sourceLocaleOptionsForAutoTranslate.some((o) => o.value === syncSourceLocale) ? syncSourceLocale : (sourceLocaleOptionsForAutoTranslate[0]?.value ?? '')}
                        onChange={(e) => setSyncSourceLocale(normalizeLanguage(e.target.value))}
                        disabled={writePending}
                        aria-label={t.common.sourceLanguage || (language === 'tr' ? 'Kaynak Dil' : 'Source Language')}
                    >
                        {sourceLocaleOptionsForAutoTranslate.length === 0 ? (
                            <option value="">{t.common.selectLanguagesFirst}</option>
                        ) : (
                            sourceLocaleOptionsForAutoTranslate.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))
                        )}
                    </select>
                </div>
                {syncError && <Alert type="error" message={syncError} />}
            </Modal>

            {/* Add/Edit Modal */}
            <Modal
                isOpen={page.showModal}
                onClose={closeCreate}
                title={labels.create}
                footer={
                    <ModalFormFooter
                        language={language as any}
                        saving={writePending}
                        onCancel={closeCreate}
                        formId="add-translation-form"
                    />
                }
            >
                <form id="add-translation-form" onSubmit={handleAddNew}>
                    <Alert type="error" message={createError} />
                    <div className="form-group mb-4">
                        <label className="form-label">{labels.key}</label>
                        <input
                            className="form-control"
                            value={newTranslation.fieldName}
                            aria-label={labels.key}
                            disabled={writePending}
                            onChange={e => setNewTranslation(p => ({ ...p, fieldName: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label className="form-label">{labels.value}</label>
                        <textarea
                            className="form-control"
                            value={newTranslation.value}
                            aria-label={labels.value}
                            disabled={writePending}
                            onChange={e => setNewTranslation(p => ({ ...p, value: e.target.value }))}
                            required
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label className="form-label">{labels.locale}</label>
                        <select
                            className="form-control"
                            value={newTranslation.locale}
                            aria-label={labels.locale}
                            disabled={writePending}
                            onChange={e => setNewTranslation(p => ({ ...p, locale: e.target.value }))}
                        >
                            {SUPPORTED_LANGUAGES.map((code) => (
                                <option key={code} value={code}>{code.toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export default Translations;
